import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Handshake, LayoutGrid, Maximize2, Minimize2, Phone, RotateCcw, Wrench, X, Beer } from "lucide-react";
import { EASE } from "@/components/motion/Reveal";
import { COMPANY } from "@/data/site";
import { getLenis } from "@/lib/lenis";
import { cn } from "@/lib/utils";
import { fetchHistory, getSession, newSession, parseReply, streamReply } from "@/components/chat/chatApi";
import { ActionRow, LeadCard, LeadSuccess, Markdown, Orb, Suggestions, Typing } from "@/components/chat/ChatBits";

const STARTERS = [
  { icon: Handshake, q: "How does revenue share work?" },
  { icon: Beer, q: "What equipment fits a bar?" },
  { icon: Wrench, q: "My claw machine is broken" },
  { icon: LayoutGrid, q: "What machines do you offer?" },
];

const Welcome = ({ onPick }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex h-full flex-col justify-end pb-2" data-testid="chat-welcome">
    <Orb className="h-14 w-14" />
    <h3 className="mt-5 font-display text-2xl font-bold leading-tight text-white">Hi, I'm Brewer AI.</h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-400">Ask me anything about equipment, revenue share partnerships, installation or service — I'll point you in the right direction.</p>
    <div className="mt-6 grid grid-cols-2 gap-2">
      {STARTERS.map(({ icon: Icon, q }, i) => (
        <motion.button key={q} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.07, ease: EASE, duration: 0.5 }} onClick={() => onPick(q)} className="group rounded-2xl border border-white/10 bg-ink-800/70 p-3.5 text-left text-xs leading-snug text-slate-300 transition-[border-color,background-color] hover:border-volt/50 hover:bg-ink-800" data-testid={`chat-suggestion-${i + 1}`}>
          <Icon className="mb-2.5 h-4 w-4 text-volt transition-transform group-hover:scale-110" />
          {q}
        </motion.button>
      ))}
    </div>
  </motion.div>
);

const Message = ({ m, i, onNav }) => {
  const user = m.role === "user";
  const { body } = user ? { body: m.content } : parseReply(m.content);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }} className={cn("flex gap-2.5", user ? "justify-end" : "justify-start")} data-testid={`chat-message-${m.role}-${i}`}>
      {!user && <Orb className="mt-0.5 h-7 w-7" spin={!body} />}
      <div className={cn("max-w-[84%] rounded-2xl px-4 py-3 text-sm leading-relaxed", user ? "rounded-br-md bg-volt font-medium text-ink-950" : "rounded-tl-md border border-white/[0.07] bg-ink-800/80 text-slate-300")}>
        {user ? body : body ? <Markdown text={body} onNav={onNav} /> : <Typing />}
      </div>
    </motion.div>
  );
};

const Header = ({ expanded, setExpanded, onReset, onClose }) => (
  <div className="flex items-center gap-3 border-b border-white/5 px-5 py-4">
    <div className="relative"><Orb className="h-10 w-10" /><span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-ink-900 bg-emerald-400" /></div>
    <div className="min-w-0 flex-1">
      <p className="font-display text-base font-semibold text-white">Brewer AI</p>
      <p className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400/80">Online now</p>
    </div>
    {[
      { icon: RotateCcw, label: "New conversation", fn: onReset, id: "chat-reset-button" },
      { icon: expanded ? Minimize2 : Maximize2, label: expanded ? "Shrink" : "Expand", fn: () => setExpanded((e) => !e), id: "chat-expand-button", cls: "hidden sm:grid" },
      { icon: X, label: "Close chat", fn: onClose, id: "chat-close-button" },
    ].map(({ icon: Icon, label, fn, id, cls }) => (
      <button key={id} onClick={fn} aria-label={label} title={label} className={cn("grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 transition-colors hover:border-white/40 hover:text-white", cls)} data-testid={id}>
        <Icon className="h-4 w-4" />
      </button>
    ))}
  </div>
);

const Panel = ({ onClose }) => {
  const { pathname } = useLocation();
  const [session, setSession] = useState(getSession);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [leadSent, setLeadSent] = useState(() => localStorage.getItem(`lead-${getSession()}`) === "1");
  const [leadAt, setLeadAt] = useState(-1);
  const scroller = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory(session).then((h) => Array.isArray(h) && setMsgs(h.map(({ role, content }) => ({ role, content })))).catch(() => {});
  }, [session]);
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    inputRef.current?.focus();
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const send = useCallback(async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const append = (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; });
    try {
      await streamReply({ session_id: session, message: q, page: pathname }, append);
    } catch {
      append(`Sorry, I couldn't connect right now. Please call **${COMPANY.phone}**.\n[[actions: call]]`);
    } finally {
      setBusy(false);
    }
  }, [busy, session, pathname]);

  const reset = () => { setSession(newSession()); setMsgs([]); setLeadSent(false); };
  const onLead = () => { localStorage.setItem(`lead-${session}`, "1"); setLeadAt(msgs.length); setLeadSent(true); };
  const last = msgs[msgs.length - 1];
  const tail = last?.role === "assistant" && !busy ? parseReply(last.content) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.94 }}
      transition={{ duration: 0.5, ease: EASE }}
      className={cn("fixed bottom-24 right-4 z-[95] flex origin-bottom-right flex-col overflow-hidden rounded-[28px] border border-white/10 bg-ink-900/95 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.9),0_0_80px_-40px_rgba(0,240,255,0.6)] backdrop-blur-2xl sm:right-6", expanded ? "h-[min(820px,calc(100svh-8rem))] w-[calc(100vw-2rem)] sm:w-[620px]" : "h-[min(640px,calc(100svh-8rem))] w-[calc(100vw-2rem)] sm:w-[420px]")}
      data-lenis-prevent
      data-testid="chat-panel"
    >
      <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-volt/10 blur-3xl" />
      <Header expanded={expanded} setExpanded={setExpanded} onReset={reset} onClose={onClose} />
      <div ref={scroller} className="relative flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="chat-messages">
        {msgs.length === 0 ? <Welcome onPick={send} /> : msgs.map((m, i) => <Message key={i} m={m} i={i} onNav={() => setExpanded(false)} />)}
        {tail && <ActionRow actions={tail.actions.filter((a) => a !== "lead")} />}
        {tail && tail.actions.includes("lead") && !leadSent && <LeadCard session={session} onDone={onLead} />}
        {leadSent && leadAt === msgs.length && <LeadSuccess />}
        {tail && <Suggestions items={tail.suggest} onPick={send} />}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-white/5 p-3">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-ink-950/60 pl-4 pr-1.5 transition-colors focus-within:border-volt/60">
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about equipment, partnering, service…" className="h-12 flex-1 bg-transparent text-sm text-white placeholder:text-slate-600 focus:outline-none" data-testid="chat-input" />
          <button type="submit" disabled={busy || !input.trim()} className="grid h-9 w-9 place-items-center rounded-full bg-volt text-ink-950 transition-[opacity,transform] hover:scale-105 disabled:opacity-30" aria-label="Send" data-testid="chat-send-button">
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between px-2 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">
          <span>AI answers may vary — confirm details with Brewer</span>
          <a href={COMPANY.tel} className="flex items-center gap-1 text-slate-500 hover:text-volt" data-testid="chat-call-button"><Phone className="h-3 w-3" /> Call</a>
        </div>
      </form>
    </motion.div>
  );
};

const Teaser = ({ onOpen, onDismiss }) => (
  <motion.div initial={{ opacity: 0, y: 12, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }} transition={{ duration: 0.5, ease: EASE }} className="fixed bottom-24 right-4 z-[95] hidden w-64 origin-bottom-right rounded-2xl rounded-br-md border border-white/10 bg-ink-900/95 p-4 shadow-2xl backdrop-blur-xl sm:right-6 sm:block" data-testid="chat-teaser">
    <button onClick={onDismiss} className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full text-slate-500 hover:text-white" aria-label="Dismiss" data-testid="chat-teaser-dismiss"><X className="h-3.5 w-3.5" /></button>
    <button onClick={onOpen} className="text-left" data-testid="chat-teaser-open">
      <p className="pr-4 text-sm font-semibold text-white">Thinking about adding games to your business?</p>
      <p className="mt-1 text-xs text-slate-400">Ask Brewer AI — answers in seconds.</p>
    </button>
  </motion.div>
);

export const BrewerAI = () => {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [gaming, setGaming] = useState(false);
  useEffect(() => {
    const on = (e) => setGaming(!!e.detail);
    window.addEventListener("claw-view", on);
    return () => window.removeEventListener("claw-view", on);
  }, []);
  useEffect(() => {
    if (sessionStorage.getItem("brewer-teaser") === "1" || window.innerWidth < 640) return;
    const t = setTimeout(() => setTeaser(true), 9000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const l = getLenis();
    if (!l) return;
    const small = window.innerWidth < 640;
    if (open && small) l.stop();
    else l.start();
  }, [open]);
  const dismiss = () => { sessionStorage.setItem("brewer-teaser", "1"); setTeaser(false); };
  const toggle = () => { dismiss(); setOpen((o) => !o); };
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <AnimatePresence>{open && <Panel onClose={close} />}</AnimatePresence>
      <AnimatePresence>{teaser && !open && !gaming && <Teaser onOpen={toggle} onDismiss={dismiss} />}</AnimatePresence>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={gaming && !open ? { scale: 0.6, opacity: 0, y: 40 } : { scale: 1, opacity: 1, y: 0 }}
        transition={{ delay: gaming ? 0 : 0.2, type: "spring", stiffness: 260, damping: 20 }}
        style={{ pointerEvents: gaming && !open ? "none" : "auto" }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        onClick={toggle}
        className="group fixed bottom-5 right-4 z-[96] flex h-14 items-center gap-3 rounded-full border border-white/10 bg-ink-900/90 pl-2 pr-5 text-sm font-semibold text-white shadow-[0_0_50px_-12px_rgba(0,240,255,0.8)] backdrop-blur-xl sm:right-6"
        data-testid="chat-launcher-button"
        aria-label={open ? "Close Brewer AI" : "Open Brewer AI"}
      >
        {open ? <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10"><X className="h-4 w-4" /></span> : <Orb className="h-10 w-10" />}
        <span className="flex flex-col items-start leading-none">
          <span>{open ? "Close" : "Ask Brewer AI"}</span>
          {!open && <span className="mt-1 font-mono text-[9px] font-normal uppercase tracking-[0.2em] text-emerald-400/80">Online now</span>}
        </span>
      </motion.button>
    </>
  );
};
